import ProfessionalContactModal from '@/components/professional/ContactDetailsModal';
import ProfessionalHit from '@/components/professional/ProfessionalHit';
import ProfessionalRequestQuoteModal from '@/components/professional/RequestQuoteModal';
import { MissingImageIcon } from '@/images/product';
import { formatPrice } from '@/lib/currency';
import { useModal } from '@/lib/modal';
import { algoliaAnalytics } from '@/lib/search';
import { getUserID } from '@/lib/tracking';
import clsx from 'clsx';
import { Link, navigate } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { FC, useEffect } from 'react';
import {
  connectHitInsights,
  connectInfiniteHits,
  connectStateResults,
  Highlight,
  PoweredBy,
} from 'react-instantsearch-dom';
import { useInView } from 'react-intersection-observer';

import './SearchResult.css';

const HitCount = connectStateResults(
  ({ searchResults, searchState, currentCategoryName }) => {
    const hitCount = searchResults && searchResults.nbHits;

    return (
      <div className="HitCount -mx-4 mt-2 lg:mt-0 lg:mb-4 px-4 lb:pb-2 lg:pt-4 font-semibold">
        <div className="opacity-50 lg:text-primary-300 lg:opacity-100 lg:text-lg">
          {hitCount}
          <span className="font-medium">
            {' rezultat'}
            {hitCount !== 1 ? 'e' : ''}
          </span>
          {searchState.query && (
            <>
              <span className="font-medium">{' pentru '}</span>
              &quot;{searchState.query}&quot;
            </>
          )}
          {currentCategoryName && (
            <>
              <span className="font-medium">{' in '}</span>
              {currentCategoryName}
            </>
          )}
        </div>
      </div>
    );
  }
);

const ProductHit = connectHitInsights(algoliaAnalytics)(({ hit, insights }) => {
  // eslint-disable-next-line no-underscore-dangle
  const pathWithTracking = `${hit.path}?qid=${hit.__queryID}`;
  return (
    <Link
      to={pathWithTracking}
      className={clsx('flex gap-4 lg:w-50 flex-col bg-white p-3 h-full')}
      onClick={(event) => {
        event.preventDefault();
        try {
          insights('clickedObjectIDsAfterSearch', {
            eventName: 'Product Clicked',
            userToken: getUserID(),
          });
        } catch {
          // ata ete n ai csf
        }
        return navigate(pathWithTracking);
      }}
    >
      {hit.image ? (
        <GatsbyImage
          className="w-32 h-32 lg:w-44 lg:h-44 self-center"
          alt={hit.name}
          image={getImage({
            childImageSharp: { gatsbyImageData: hit.image },
          } as any)}
          objectFit="contain"
        />
      ) : (
        <MissingImageIcon className="w-32 h-32 lg:w-44 lg:h-44 text-gray-250 self-center" />
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="text-gray-500 font-semibold flex-1">
          <Highlight attribute="name" hit={hit} tagName="mark" />
        </div>
        <div className="text-xs opacity-50 truncate lg:flex-1">
          <Highlight attribute="code" hit={hit} tagName="mark" />
        </div>
        <div className="text-base mt-2">
          <div className="font-semibold text-red">
            {formatPrice(
              hit.averageMinPrice.value,
              hit.averageMinPrice.currency,
              false,
              {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }
            )}
            {' - '}
            {formatPrice(
              hit.averageMaxPrice.value,
              hit.averageMaxPrice.currency,
              false,
              {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }
            )}
          </div>
        </div>
      </div>
    </Link>
  );
});

const SupplierHit = connectHitInsights(algoliaAnalytics)(
  ({ hit, insights }) => {
    // eslint-disable-next-line no-underscore-dangle
    const pathWithTracking = `${hit.path}?qid=${hit.__queryID}`;
    return (
      <Link
        to={pathWithTracking}
        className={clsx('flex gap-4 lg:w-50 flex-col bg-white p-3 h-full')}
        onClick={(event) => {
          event.preventDefault();
          try {
            insights('clickedObjectIDsAfterSearch', {
              eventName: 'Supplier Clicked',
              userToken: getUserID(),
            });
          } catch {
            // ata ete n ai csf
          }
          return navigate(pathWithTracking);
        }}
      >
        {hit.image ? (
          <GatsbyImage
            className="w-32 h-32 lg:w-44 lg:h-44 self-center"
            alt={hit.name}
            image={getImage({
              childImageSharp: { gatsbyImageData: hit.image },
            } as any)}
            objectFit="contain"
          />
        ) : (
          <MissingImageIcon className="w-32 h-32 lg:w-44 lg:h-44 text-gray-250 self-center" />
        )}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="text-gray-500 font-semibold flex-1">
            <Highlight attribute="name" hit={hit} tagName="mark" />
          </div>
          <div className="text-xs opacity-50 truncate lg:flex-1">
            <Highlight attribute="contactPerson" hit={hit} tagName="mark" />
          </div>
        </div>

        {hit.category && (
          <div className="bg-yellow text-center mt-4 -mx-3 -mb-3 px-4 py-2">
            {hit.category}
          </div>
        )}
      </Link>
    );
  }
);

const InfiniteHits = connectInfiniteHits(
  ({ hits, hasMore, refineNext, hitType }) => {
    const { ref: endOfResultsRef, inView: endInView } = useInView();

    const professionalContactModal = useModal();
    const professionalRequestQuoteModal = useModal();

    useEffect(() => {
      if (endInView && hasMore) {
        refineNext();
      }
    }, [endInView, hasMore]);

    return (
      <>
        <div className={clsx('InfiniteHits', `InfiniteHits-${hitType}`)}>
          {hits.map((hit) =>
            hitType === 'product' ? (
              <ProductHit key={hit.objectID} hit={hit} />
            ) : hitType === 'supplier' ? (
              <SupplierHit key={hit.objectID} hit={hit} />
            ) : (
              <ProfessionalHit
                key={hit.objectID}
                hit={hit}
                contactModal={professionalContactModal}
                requestQuoteModal={professionalRequestQuoteModal}
              />
            )
          )}
        </div>
        <div className="" ref={endOfResultsRef} />

        <ProfessionalContactModal control={professionalContactModal} />
        <ProfessionalRequestQuoteModal
          control={professionalRequestQuoteModal}
        />
      </>
    );
  }
);

type SearchResultProps = {
  hitType: 'product' | 'supplier' | 'professional';
  className?: string;
  currentCategoryName?: string;
};

const SearchResult: FC<SearchResultProps> = ({
  className,
  hitType,
  children,
  currentCategoryName,
}) => (
  <div className={clsx('SearchResult px-4 lg:pl-8 pb-4', className)}>
    <HitCount currentCategoryName={currentCategoryName} />

    {children}

    <InfiniteHits hitType={hitType} />

    <div className="mb-4" />
    <PoweredBy />
  </div>
);

export default SearchResult;
